import React, {useState, useEffect, useRef} from 'react';
import {Box} from '@mui/material';
import {v4 as uuidv4} from 'uuid';
import './ChipPanel.scss';
import Chip from './chip/Chip';
import {Criteria} from '../../../../types/Criteria';
import {useAutocomplete} from '../../../../../../hooks/session-search/useSessionSearch';
import ArrAutocomplete from '../../../../../common/arr-autocomplete/ArrAutocomplete';
import {formatAutocompleteResults} from '../../autocompleteUtils';

export interface ChipPanelProps {
  criteria: Criteria[];
  onCriteriaChange?: (updatedCriteria: Criteria[]) => void;
  dataTestId: string;
}

const ChipPanel: React.FC<ChipPanelProps> = ({
  criteria,
  onCriteriaChange,
  dataTestId,
}) => {
  const [chips, setChips] = useState(criteria);
  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const [autocompleteEnabled, setAutocompleteEnabled] =
    useState<boolean>(false);
  const [autocompleteItems, setAutocompleteItems] = useState<string[]>([]);
  const {data, refetch} = useAutocomplete(inputValue);
  const lastFocusedChipRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setChips(criteria);
    setAutocompleteItems([]);
  }, [criteria]);

  useEffect(() => {
    refetch();
  }, [inputValue]);

  useEffect(() => {
    const suggestions = formatAutocompleteResults(data ?? []);
    setAutocompleteItems(suggestions);
  }, [data]);

  const handleDelete = (criteria: Criteria) => {
    deleteCriteria(criteria);
  };

  const handleEdit = (criteria: Criteria) => {
    setChips((prev) => {
      const updated = prev.map((c) => (c.id === criteria.id ? criteria : c));

      // Call onCriteriaChange
      onCriteriaChange?.(updated);

      return updated;
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePanelClick = (e: React.MouseEvent<HTMLDivElement>) => {
    setInputVisible(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (inputValue) {
        addCriteria();
      } else {
        clearCriteria();
      }
    } else if (e.key === 'Escape') {
      clearCriteria();
    }
  };

  const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
    // Updates the width so the input shrinks/grows based on the content
    // CSS could do this better but 'field-sizing: content;' is not supported by Safari or Firefox
    const paddingLeft = 8;
    const paddingRight = 8;
    const defaultWidth = 50 - paddingLeft - paddingRight;
    e.currentTarget.style.width = `${defaultWidth}px`;
    e.currentTarget.style.width = `${e.currentTarget.scrollWidth}px`;

    setInputValue(e.currentTarget.value);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (inputValue) {
      addCriteria();
    } else {
      clearCriteria();
    }
  };

  const addCriteria = () => {
    const newChip: Criteria = {
      id: uuidv4(),
      type: 'Operand',
      value: inputValue,
    };

    const updated = [...chips, newChip];
    setChips(updated);

    setInputVisible(false);
    setInputValue('');

    onCriteriaChange && onCriteriaChange(updated);
  };

  const clearCriteria = () => {
    setInputVisible(false);
    setInputValue('');
  };

  const deleteCriteria = (criteria: Criteria) => {
    const updated = chips.filter((chip) => chip.id !== criteria.id);
    setChips(updated);
    onCriteriaChange?.(updated);
  };

  return (
    <Box
      className="chip-panel"
      onClick={handlePanelClick}
      role="group"
      aria-label="Search chip panel"
      data-testid={`chip-panle-clickable-${dataTestId}`}
      tabIndex={0}
    >
      <Box className="chip-row" data-testid={`chip-row-${dataTestId}`}>
        {chips.map((criteria, index) => (
          <Chip
            key={criteria.id}
            criteria={criteria}
            onEdit={handleEdit}
            onDelete={handleDelete}
            dataTestId={`chip-panel-${dataTestId}-${index}`}
            chipFocusRef={lastFocusedChipRef}
          />
        ))}
        {inputVisible && (
          <ArrAutocomplete
            className="chip-autocomplete"
            value={inputValue}
            options={autocompleteItems}
            onChange={setInputValue}
            enabled={autocompleteEnabled}
            data-testid={`chip-panel-autocomplete--${dataTestId}`}
          >
            <input
              type="text"
              className="chip-input"
              aria-label="Add chip"
              autoFocus
              value={inputValue}
              onFocus={() => setAutocompleteEnabled(true)}
              onBlur={(e) => {
                handleBlur(e);
                setAutocompleteEnabled(false);
              }}
              onKeyDown={handleKeyDown}
              onChange={handleChange}
              onInput={handleInput}
              data-testid={`chip-panel-input--${dataTestId}`}
            />
          </ArrAutocomplete>
        )}
      </Box>
    </Box>
  );
};

export default ChipPanel;
